import { useEffect, useState } from 'react'
import { BarChart3, Inbox, Mail, PenLine, Send } from 'lucide-react'
import { api } from '../../api/client'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

type DailyStat = { date: string; count: number }
type UserDashboardData = {
  email: string
  assignedMailbox: string
  totalMessages: number
  inboxCount: number
  sentCount: number
  draftCount: number
  unreadCount: number
  sentDaily: DailyStat[]
  receivedDaily: DailyStat[]
}

function MiniChart({ data, color }: { data: DailyStat[]; color: string }) {
  if (data.length === 0) return <p className="text-xs text-muted-foreground mt-2">No data</p>
  const max = Math.max(...data.map(d => d.count), 1)
  return (
    <div className="flex items-end gap-1 h-20 mt-3">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
          <div className={`w-full rounded-sm ${color} transition-all`} style={{ height: `${Math.max((d.count / max) * 100, 4)}%` }} />
          <span className="text-[9px] text-muted-foreground leading-none">{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  )
}

export function UserDashboardPage() {
  const { t } = useI18n()
  const [data, setData] = useState<UserDashboardData | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<UserDashboardData>('/user/dashboard').then(setData).catch((e: Error) => setError(e.message))
  }, [])

  if (error) return <p className="rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>
  if (!data) return <p className="text-muted-foreground text-sm">{t('common.loading')}</p>

  const statCards = [
    { label: t('user.inbox'), value: data.inboxCount, icon: Inbox, color: 'text-blue-600 bg-blue-50' },
    { label: t('user.sent'), value: data.sentCount, icon: Send, color: 'text-emerald-600 bg-emerald-50' },
    { label: t('user.drafts'), value: data.draftCount, icon: PenLine, color: 'text-amber-600 bg-amber-50' },
    { label: t('user.unread'), value: data.unreadCount, icon: Mail, color: 'text-rose-600 bg-rose-50' },
  ]

  return <>
    <PageHeader title="user.dashboard" />

    {/* Email info */}
    <div className="panel p-5 mb-5">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Mail size={20} />
        </span>
        <div>
          <p className="text-sm text-muted-foreground">{t('user.assignedEmail')}</p>
          <p className="font-semibold">{data.assignedMailbox || t('user.noMailboxAssigned')}</p>
        </div>
      </div>
    </div>

    {/* Stat cards */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
      {statCards.map(({ label, value, icon: Icon, color }) => (
        <div key={label} className="panel p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${color}`}><Icon size={16} /></span>
            <span className="text-xs text-muted-foreground">{label}</span>
          </div>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      ))}
    </div>

    {/* Charts */}
    <div className="grid gap-5 sm:grid-cols-2">
      <div className="panel p-5">
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="text-emerald-600" />
          <h3 className="text-sm font-semibold">{t('user.sentChart')}</h3>
        </div>
        <p className="text-xs text-muted-foreground">{t('user.last14Days')}</p>
        <MiniChart data={data.sentDaily} color="bg-emerald-400" />
      </div>
      <div className="panel p-5">
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="text-blue-600" />
          <h3 className="text-sm font-semibold">{t('user.receivedChart')}</h3>
        </div>
        <p className="text-xs text-muted-foreground">{t('user.last14Days')}</p>
        <MiniChart data={data.receivedDaily} color="bg-blue-400" />
      </div>
    </div>
  </>
}
